const TAB_SELECTOR = ".contact-form-tab";
const TRIGGER_SELECTOR = "[data-form-tab]";
const PANEL_SELECTOR = ".contact-form-tab__panel";
const REQUIRED_SELECTOR = '[required], [aria-required="true"]';

const contactFormTabs = () => {
	document.querySelectorAll("[data-contact-multistep]").forEach((component) => {
		const tabs = [...component.querySelectorAll(TAB_SELECTOR)];
		if (!tabs.length) return;

		let activeIndex = Math.max(0, tabs.findIndex((tab) => tab.classList.contains("is-active")));

		const activate = (nextIndex, focusPanel = false) => {
			if (nextIndex < 0 || nextIndex >= tabs.length || tabs[nextIndex].classList.contains("is-locked")) return;

			activeIndex = nextIndex;
			tabs.forEach((tab, index) => {
				const isActive = index === nextIndex;
				const trigger = tab.querySelector(TRIGGER_SELECTOR);
				const panel = tab.querySelector(PANEL_SELECTOR);

				tab.classList.toggle("is-active", isActive);
				trigger?.setAttribute("aria-selected", String(isActive));
				trigger?.setAttribute("aria-expanded", String(isActive));

				if (panel) {
					panel.hidden = !isActive;
					panel.inert = !isActive;
					panel.setAttribute("aria-hidden", String(!isActive));
				}
			});

			if (focusPanel) {
				window.setTimeout(() => focusFirstControl(tabs[nextIndex]), prefersReducedMotion() ? 0 : 450);
			}

			component.dispatchEvent(new CustomEvent("contactformtabs:change", {
				detail: { step: nextIndex + 1 },
			}));
		};

		const unlock = (index) => {
			const tab = tabs[index];
			if (!tab) return;

			tab.classList.remove("is-locked");
			tab.classList.add("is-unlocked");
			tab.querySelector(TRIGGER_SELECTOR)?.setAttribute("aria-disabled", "false");
		};

		const lockAfter = (index) => {
			tabs.forEach((tab, tabIndex) => {
				if (tabIndex <= index) return;
				tab.classList.remove("is-active", "is-completed", "is-unlocked");
				tab.classList.add("is-locked");
				const trigger = tab.querySelector(TRIGGER_SELECTOR);
				const panel = tab.querySelector(PANEL_SELECTOR);
				trigger?.setAttribute("aria-disabled", "true");
				trigger?.setAttribute("aria-selected", "false");
				trigger?.setAttribute("aria-expanded", "false");
				if (panel) {
					panel.hidden = true;
					panel.inert = true;
					panel.setAttribute("aria-hidden", "true");
				}
			});
		};

		const advance = () => {
			const currentTab = tabs[activeIndex];
			if (!validateTab(currentTab)) return;

			currentTab.classList.add("is-completed");
			unlock(activeIndex + 1);
			activate(activeIndex + 1, true);
		};

		tabs.forEach((tab, index) => {
			const trigger = tab.querySelector(TRIGGER_SELECTOR);
			const panel = tab.querySelector(PANEL_SELECTOR);
			const isActive = index === activeIndex;

			trigger?.setAttribute("aria-selected", String(isActive));
			trigger?.setAttribute("aria-expanded", String(isActive));
			trigger?.setAttribute("aria-disabled", String(tab.classList.contains("is-locked")));

			if (panel) {
				panel.hidden = !isActive;
				panel.inert = !isActive;
				panel.setAttribute("aria-hidden", String(!isActive));
			}

			trigger?.addEventListener("click", () => {
				if (tab.classList.contains("is-locked")) {
					advance();
					return;
				}

				activate(index, false);
			});

			panel?.querySelectorAll("[data-form-next]").forEach((button) => button.addEventListener("click", advance));
			panel?.querySelectorAll("[data-form-prev]").forEach((button) => {
				button.addEventListener("click", () => activate(Math.max(0, activeIndex - 1), true));
			});

			panel?.addEventListener("input", (event) => {
				if (!event.target.matches(REQUIRED_SELECTOR) || !tab.classList.contains("is-completed")) return;
				if (validateTab(tab, false)) return;
				tab.classList.remove("is-completed");
				lockAfter(index);
			});
		});
		const cf7 = component.closest(".wpcf7");

		cf7?.addEventListener("wpcf7invalid", () => {
			component.querySelectorAll(".contact-form-field-error").forEach((error) => {
				const wrap = error.parentElement;
				const hasCf7Error = [...wrap.querySelectorAll(".wpcf7-not-valid-tip")]
					.some((tip) => tip !== error);

				if (hasCf7Error) error.remove();
			});
		});

		component.addEventListener("contactformtabs:reset", () => {
			tabs.forEach((tab) => {
				tab.classList.remove("is-completed");
			});

			component.querySelectorAll("input, textarea, select").forEach((field) => {
				clearFieldError(field);
			});

			unlock(0);
			activate(0);
			lockAfter(0);
		});

		unlock(0);
		activate(activeIndex);
	});
};

const validateTab = (tab, showErrors = true) => {
	const panel = tab?.querySelector(PANEL_SELECTOR);
	if (!panel) return true;

	const fields = [...panel.querySelectorAll("input, textarea, select")].filter((field) => {
		return field.type !== "hidden" && !field.disabled;
	});
	let firstInvalid;

	fields.forEach((field) => clearFieldError(field));

	fields.forEach((field) => {
		const isRequired = field.matches(REQUIRED_SELECTOR);
		const isCheckable = field.type === "checkbox" || field.type === "radio";
		let isValid = field.checkValidity();

		if (isRequired && isCheckable && field.name) {
			const group = fields.filter((item) => item.name === field.name);
			isValid = group.some((item) => item.checked);
		} else if (isRequired && !String(field.value).trim()) {
			isValid = false;
		}

		if (isValid) return;
		firstInvalid ||= field;
		if (showErrors) setFieldError(field);
	});

	if (firstInvalid && showErrors) firstInvalid.focus();
	return !firstInvalid;
};

const getFieldMessages = (field) => {
	const component = field.closest("[data-contact-multistep]");
	const language = (
		component?.dataset.formLanguage ||
		document.documentElement.lang ||
		"es"
	).toLowerCase().split("-")[0];

	const messages = {
		es: {
			required: "Este campo es obligatorio.",
			invalid: "Introduce un formato válido.",
		},
		ca: {
			required: "Aquest camp és obligatori.",
			invalid: "Introdueix un format vàlid.",
		},
		en: {
			required: "This field is required.",
			invalid: "Please enter a valid format.",
		},
	};

	return messages[language] || messages.es;
};

const setFieldError = (field) => {
	field.classList.add("wpcf7-not-valid");
	field.setAttribute("aria-invalid", "true");

	const wrap = field.closest(".wpcf7-form-control-wrap") || field.parentElement;
	if (!wrap || wrap.querySelector(".contact-form-field-error")) return;

	const messages = getFieldMessages(field);
	const isEmpty = !String(field.value).trim();
	const isCheckable = field.type === "checkbox" || field.type === "radio";

	const error = document.createElement("span");
	error.className = "contact-form-field-error wpcf7-not-valid-tip";
	error.setAttribute("role", "alert");
	error.textContent = isEmpty || isCheckable
		? messages.required
		: messages.invalid;

	wrap.append(error);
};

const clearFieldError = (field) => {
	field.classList.remove("wpcf7-not-valid");
	field.removeAttribute("aria-invalid");

	const wrap = field.closest(".wpcf7-form-control-wrap") || field.parentElement;

	wrap?.querySelectorAll(".wpcf7-not-valid-tip").forEach((error) => {
		error.remove();
	});
};

const focusFirstControl = (tab) => {
	const control = tab.querySelector(`${PANEL_SELECTOR} input, ${PANEL_SELECTOR} textarea, ${PANEL_SELECTOR} select, ${PANEL_SELECTOR} button`);
	(control || tab.querySelector(TRIGGER_SELECTOR))?.focus({ preventScroll: true });
};

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export { contactFormTabs };
